import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { userService, User } from '../services/userService';
import UserLookup from './UserLookup';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

interface AttachUsersModalProps {
    clientId: number;
    clientName: string;
    onClose: () => void;
    onSuccess?: () => void;
}

const AttachUsersModal: React.FC<AttachUsersModalProps> = ({
    clientId,
    clientName,
    onClose,
    onSuccess,
}) => {
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const queryClient = useQueryClient();

    const attachUsersMutation = useMutation({
        mutationFn: async (users: User[]) => {
            // Process all users in parallel
            const updatePromises = users.map((user) =>
                userService.updateUser(user.id, {
                    // Add access to a single client without replacing existing mappings.
                    appendClientIds: [clientId],
                })
            );

            return Promise.all(updatePromises);
        },
        onSuccess: (results) => {
            queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
            toast.success(`Successfully attached ${results.length} users to ${clientName}`);
            if (onSuccess) onSuccess();
            onClose();
        },
        onError: (error: any) => {
            console.error("Error attaching users", error);
            toast.error('Failed to attach some users. Please try again.');
        },
    });

    const handleAddUser = (user: User) => {
        // Check if user is already selected
        if (selectedUsers.some((u) => u.id === user.id)) {
            toast.error('User already selected');
            return;
        }

        // Check if user is already attached to this client
        const isAlreadyAttached =
            user.clientId === clientId ||
            user.clientAccess?.some((access) => access.clientId === clientId);

        if (isAlreadyAttached) {
            toast.error(`${user.firstName} is already attached to this client`);
            return;
        }

        setSelectedUsers([...selectedUsers, user]);
    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                        Attach Users to {clientName}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                    Search for users to grant them access to this client.
                </p>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search Users
                    </label>
                    <UserLookup
                        onSelect={handleAddUser}
                        excludeUserIds={selectedUsers.map(u => u.id)}
                        placeholder="Type name or email to search..."
                    />
                </div>

                {selectedUsers.length > 0 && (
                    <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Selected Users ({selectedUsers.length})
                        </h4>
                        <div className="bg-gray-50 border border-gray-200 rounded-md max-h-60 overflow-y-auto divide-y divide-gray-200">
                            {selectedUsers.map((user) => (
                                <div key={user.id} className="p-3 flex justify-between items-center">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                            <UserIcon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {user.firstName} {user.lastName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {user.email}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveUser(user.id)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => attachUsersMutation.mutate(selectedUsers)}
                        disabled={selectedUsers.length === 0 || attachUsersMutation.isLoading}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        {attachUsersMutation.isLoading && (
                            <LoadingSpinner size="sm" className="mr-2" />
                        )}
                        Attach {selectedUsers.length > 0 ? `${selectedUsers.length} Users` : 'Users'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AttachUsersModal;
